// Палитра команд (Ctrl+K): переход куда угодно, действия, «спросить агента».
import { applyTheme, isDark } from '@autoaff/ui';
import { useNavigate } from '@tanstack/react-router';
import { Bot, MessageCircle, Moon, Search, Settings2, SquarePen } from 'lucide-react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { chatStore } from '../chat/store';
import { SETTINGS_CARDS, SETTINGS_TABS } from '../pages/settings-nav';
import { SECTIONS } from './nav';

interface Item {
  group: string;
  label: string;
  icon: typeof Search;
  run: () => void;
}

export function Palette({ onClose }: { onClose: () => void }) {
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  useEffect(() => input.current?.focus(), []);

  const items = useMemo<Item[]>(() => {
    const query = q.trim();
    const ql = query.toLowerCase();
    const all: Item[] = [
      ...SECTIONS.map((s) => ({
        group: 'Перейти',
        label: s.label,
        icon: s.icon,
        run: () => navigate({ to: s.to }),
      })),
      {
        group: 'Действия',
        label: 'Новый чат с агентом',
        icon: SquarePen,
        run: () => chatStore.open({ threadId: null }),
      },
      { group: 'Действия', label: 'Все чаты с агентом', icon: Bot, run: () => navigate({ to: '/agent' }) },
      {
        group: 'Действия',
        label: 'Сменить тему',
        icon: Moon,
        run: () => applyTheme(isDark() ? 'light' : 'dark'),
      },
    ].filter((x) => !ql || x.label.toLowerCase().includes(ql));
    // Вкладки и карточки настроек — только в поиске («бэкап», «токен», «тема»), чтобы не удлинять список
    if (ql) {
      const seen = new Set<string>();
      const add = (label: string, icon: Item['icon'], run: () => void) => {
        if (seen.has(label)) return;
        seen.add(label);
        all.push({ group: 'Настройки', label, icon, run });
      };
      for (const t of SETTINGS_TABS)
        if (t.label.toLowerCase().includes(ql))
          add(`Настройки → ${t.label}`, t.icon, () => navigate({ to: '/settings', search: { tab: t.id } }));
      for (const c of SETTINGS_CARDS) {
        if (!`${c.label} ${c.find}`.toLowerCase().includes(ql)) continue;
        const tab = SETTINGS_TABS.find((t) => t.id === c.tab);
        // Карточка с именем своей вкладки («Автономия и деньги») — это и есть вкладка
        const label = tab?.label === c.label ? `Настройки → ${c.label}` : `${tab?.label} → ${c.label}`;
        add(label, tab?.icon ?? Settings2, () =>
          navigate({ to: '/settings', search: { tab: c.tab }, hash: c.id }),
        );
      }
    }
    const ask: Item = query
      ? {
          group: 'Агент',
          label: `Спросить агента: «${query}»`,
          icon: MessageCircle,
          run: () => chatStore.open({ threadId: null, draft: query }),
        }
      : { group: 'Агент', label: 'Спросить агента…', icon: MessageCircle, run: () => chatStore.open() };
    return [ask, ...all];
  }, [q, navigate]);

  const run = (i: number) => {
    const it = items[i];
    onClose();
    it?.run();
  };

  let lastGroup = '';
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: клик по фону закрывает палитру; с клавиатуры — Esc
    <div
      className="pal"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="pal-box" role="dialog" aria-label="Палитра команд">
        <div className="pal-in">
          <Search />
          <input
            ref={input}
            value={q}
            id="palette-input"
            role="combobox"
            aria-expanded="true"
            aria-controls="pal-list"
            aria-activedescendant={items[sel] ? `pal-opt-${sel}` : undefined}
            aria-label="Команда или вопрос агенту"
            placeholder="Куда перейти или что спросить у агента…"
            autoComplete="off"
            onChange={(e) => {
              setQ(e.target.value);
              setSel(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSel((s) => (s + 1) % items.length);
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSel((s) => (s - 1 + items.length) % items.length);
              } else if (e.key === 'Enter') {
                e.preventDefault();
                run(sel);
              } else if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
              }
            }}
          />
          <kbd>Esc</kbd>
        </div>
        <div className="pal-list" id="pal-list" role="listbox" aria-label="Результаты">
          {items.map((it, i) => {
            const head = it.group !== lastGroup;
            lastGroup = it.group;
            const Icon = it.icon;
            return (
              <Fragment key={`${it.group}-${it.label}`}>
                {head && (
                  <div className="pal-group" role="presentation">
                    {it.group}
                  </div>
                )}
                <button
                  type="button"
                  id={`pal-opt-${i}`}
                  className="pal-item"
                  role="option"
                  tabIndex={-1}
                  aria-selected={i === sel}
                  onMouseEnter={() => setSel(i)}
                  onClick={() => run(i)}
                >
                  <Icon />
                  <span>{it.label}</span>
                  {i === sel && <span className="k">Enter</span>}
                </button>
              </Fragment>
            );
          })}
        </div>
        <div className="pal-foot">
          <span>↑↓ — выбрать</span>
          <span>Enter — открыть</span>
          <span>Esc — закрыть</span>
        </div>
      </div>
    </div>
  );
}
